import { z } from "zod";

/**
 * GÜVENLİK AYARLARI KATALOĞU — F-SEC-07
 *
 * ================================================================
 * NEDEN KATALOG, NEDEN JSON DEĞİL
 * ================================================================
 * Bu depo aynı problemi **üç kez** aynı şekilde çözmüş:
 *
 *   - izin kataloğu (`packages/authz`) + `RolePermission`
 *   - olay kataloğu (`audit/events.ts`) + `ActivityEvent`
 *   - talep kategorileri (izin **kayıtta**) + `StudentRequest`
 *
 * Dördüncüsü budur. Alternatif olan `Settings.preferences` JSON'u
 * şema doğrulaması yapmaz: bir yazım hatası sessizce varsayılana
 * düşer ve F-SEC-07'nin *"geçersiz değer reddedilir"* kuralı
 * uygulanamaz.
 *
 * Kataloğun asıl kazandırdığı şey ekranda: **konsol bu listeden
 * çiziliyor.** Yani "ayar eklendi ama ekranda yok" hâli yapısal olarak
 * oluşamıyor — 05.09.2026'da bu depoda yedi kez bulunan
 * "mekanizma var, besleyen yok" sınıfı bu modülde imkânsız.
 *
 * ================================================================
 * TAVAN YOK — AMA GEÇERSİZ DEĞER DE YOK (Emre'nin kararı)
 * ================================================================
 * Emre'ye taban/tavan önerildi ve **reddetti**: *"Sınır olmasın, tam
 * serbestlik."* Bedelini seçenek metninde gördü ve bilerek seçti.
 *
 * Buradaki doğrulayıcılar **tavan koymuyor**; yalnızca değerin
 * *çalışabilir* olduğunu arıyor. Ayrım önemli: **"kural koymuyoruz"**
 * ile **"saçma değeri kabul ediyoruz"** aynı şey değil. Oturum ömrü
 * `0`, sistemi kendi kendine kilitler; kilit eşiği `-3` hiçbir şey
 * ifade etmez.
 *
 * ================================================================
 * BURAYA HER ŞEY YAZILMAZ — "AYAR" OLMAK BİR KARAR
 * ================================================================
 * `AUTH_SECRET`, `TOTP_ENCRYPTION_KEY` ve `PROTECTED_NOTE_KEY`
 * kataloğa **girmiyor**. Ortak yanları: izi yok edebilmeleri ya da
 * veriyi kurtarılamaz hâle getirebilmeleri. TOTP anahtarı değişirse
 * herkesin 2FA'sı bozulur; not anahtarı değişirse çocukların
 * danışmanlık notları okunamaz olur.
 *
 * `TOTP_BASAMAK` / `TOTP_PERIYOT` / `TOTP_PENCERE` de girmiyor ama
 * sebebi farklı: bunlar bir **güvenlik seviyesi** değil, RFC 6238
 * **uyumluluk sözleşmesi**. Değiştirmek korumayı gevşetmiyor,
 * kullanıcının telefonundaki kodu geçersiz kılıyor.
 *
 * > "Tavan yok" kararı, bir şeyin **ayar olduğu** varsayımına
 * > dayanıyor. Bir alanı ayar saymak, kararı sorulmamış bir alana
 * > taşımaktır.
 */

/** Konsolun bölümleri — F-SEC-02/03/04. Ekran gruplamayı buradan alıyor. */
export const AYAR_BOLUMLERI = ["giris", "oturum", "kimlik"] as const;
export type AyarBolumu = (typeof AYAR_BOLUMLERI)[number];

export const BOLUM_BASLIGI: Record<AyarBolumu, string> = {
  giris: "Giriş koruması",
  oturum: "Oturum politikası",
  kimlik: "Kimlik, şifre ve cihaz",
};

export interface GuvenlikAyariTanimi {
  /**
   * Kalıcı kimlik. Veritabanında bu dize duruyor, dolayısıyla
   * **yeniden adlandırmak bir göç işidir** — sabit adı değiştirmek
   * yetmez.
   */
  kod: string;
  bolum: AyarBolumu;
  /** Ekranda görünen ad. */
  ad: string;
  /**
   * NE İŞE YARADIĞI DEĞİL, NEDEN VAR OLDUĞU.
   *
   * Ekranda gösteriliyor: bir güvenlik ayarını değiştiren kişi neyi
   * gevşettiğini bilmeli. "Kilit eşiği" yazıp bırakmak, sayıyı
   * büyütmenin ne anlama geldiğini söylemiyor.
   */
  aciklama: string;
  /** Ekranda sayının yanında yazan birim. Boş olabilir. */
  birim: string;
  varsayilan: number;
  /**
   * Değer geçerli mi. **Tavan yok**; yalnızca çalışabilirlik.
   * `z.coerce` kullanılıyor çünkü değer veritabanında metin duruyor.
   */
  dogrulayici: z.ZodType<number>;
}

/** Pozitif tam sayı — süre ve sayı alanlarının ortak kuralı. */
const pozitifTam = (birim: string): z.ZodType<number> =>
  z.coerce
    .number({ invalid_type_error: "Sayı girilmeli." })
    .int(`${birim} tam sayı olmalı.`)
    .positive(`${birim} sıfırdan büyük olmalı.`);

export const GUVENLIK_AYARLARI: readonly GuvenlikAyariTanimi[] = [
  // ---------------------------------------------------------------
  // F-SEC-02 — GİRİŞ KORUMASI
  // ---------------------------------------------------------------
  {
    kod: "giris.kilit_esigi",
    bolum: "giris",
    ad: "Kilit eşiği",
    aciklama:
      "Kaç başarısız denemeden sonra hesap kilitlenir. Büyütmek, şifre deneme " +
      "saldırısına daha fazla deneme hakkı verir.",
    birim: "deneme",
    varsayilan: 5,
    dogrulayici: pozitifTam("Eşik"),
  },
  {
    kod: "giris.taban_kilit_dakika",
    bolum: "giris",
    ad: "Taban kilit süresi",
    aciklama:
      "İlk kilitlenmede hesabın kapalı kalacağı süre. Tekrarlanan kilitlerde " +
      "üstel olarak artıyor.",
    birim: "dakika",
    varsayilan: 15,
    dogrulayici: pozitifTam("Süre"),
  },
  {
    kod: "giris.azami_kilit_dakika",
    bolum: "giris",
    ad: "Azami kilit süresi",
    aciklama:
      "Üstel artışın tavanı. Bunu küçültmek saldırgana daha sık deneme " +
      "penceresi açar; büyütmek meşru kullanıcıyı daha uzun süre dışarıda bırakır.",
    birim: "dakika",
    varsayilan: 24 * 60,
    dogrulayici: pozitifTam("Süre"),
  },
  {
    kod: "giris.kilit_ustel_katsayi",
    bolum: "giris",
    ad: "Kilit süresi artış katsayısı",
    aciklama:
      "Tekrarlanan her kilitte sürenin kaç katına çıkacağı. 1 yazılırsa kilit süresi " +
      "ARTMAZ: her kilit taban süre kadar kalır ve tekrarlanan saldırıya ek ceza " +
      "uygulanmaz.",
    birim: "kat",
    varsayilan: 2,
    /*
      ALT SINIR 1 — VE BU BİR GÜVENLİK TAVANI DEĞİL.

      `1` bir ZAYIFLATMA: sistem çalışır, kilit çalışır, yalnızca ceza
      artmaz. Emre'nin "tam serbestlik" kararı bunu kapsıyor ve kabul
      ediliyor.

      `1`in ALTI ise ters çalışma: kilit her tekrarda KISALIR, yani
      tekrarlanan saldırı DAHA AZ cezalanır. Bu bir güvenlik tercihi
      değil, mekanizmanın kendi amacının tersi — oturum ömrüne `0`
      yazmakla aynı sınıf.

      Tam sayı ZORUNLU DEĞİL: 1.5 anlamlı bir tercih (yavaş artış).
    */
    dogrulayici: z.coerce
      .number({ invalid_type_error: "Sayı girilmeli." })
      .min(1, "Katsayı 1'den küçük olamaz — kilit süresi her tekrarda kısalırdı."),
  },
  {
    kod: "giris.hiz_limiti",
    bolum: "giris",
    ad: "Kimlik uçlarında hız sınırı",
    aciklama:
      "Giriş, şifre sıfırlama ve ikinci aşama uçlarında bir kullanıcı ya da IP'nin " +
      "pencere başına atabileceği istek sayısı. Meşru kullanıcı dakikada bir-iki istek " +
      "atar; saldırgan yüzlerce.",
    birim: "istek",
    varsayilan: 10,
    dogrulayici: pozitifTam("Sınır"),
  },
  {
    kod: "giris.hiz_penceresi_saniye",
    bolum: "giris",
    ad: "Hız sınırı penceresi",
    aciklama:
      "Yukarıdaki sayının hangi süre içinde geçerli olduğu. Pencereyi büyütmek sınırı " +
      "sıkılaştırır, küçültmek gevşetir.",
    birim: "saniye",
    varsayilan: 60,
    dogrulayici: pozitifTam("Süre"),
  },
  /*
    GENEL API HIZ SINIRI (`kisa` ve `uzun`) KATALOGDA YOK — bilerek.

    Onlar bir GİRİŞ KORUMASI değil, kapasite/kötüye kullanım koruması:
    panelin en yoğun ekranı tek açılışta ~10 istek atıyor ve sınır ona
    göre seçilmiş. Güvenlik konsolundan yönetilmesi iki ayrı konuyu
    karıştırırdı — birini güvenlik gerekçesiyle sıkan kişi, farkında
    olmadan haftalık program ekranını kırabilirdi.

    F-SEC-02 "IP hız sınırı kuralları"nı GİRİŞ KORUMASI başlığı altında
    sayıyor; karşılığı yukarıdaki iki ayar.
  */
  {
    kod: "giris.ikinci_asama_deneme",
    bolum: "giris",
    ad: "İkinci aşama deneme sınırı",
    aciklama: "2FA kodunda kaç hata sonrası giriş bileti iptal edilir ve baştan giriş istenir.",
    birim: "deneme",
    varsayilan: 5,
    dogrulayici: pozitifTam("Sınır"),
  },
  /*
    ================================================================
    reCAPTCHA EŞİĞİ BURADA YOK — VE BU BİR EKSİK DEĞİL
    ================================================================
    İlk hâlinde vardı. Ölçüldü (05.09.2026): eşiğin ZATEN bir evi var —
    entegrasyon kaydının `config.esik` alanı, `sistem.entegrasyon.duzenle`
    iznindeki `POST /auth/recaptcha/ayarla` ucundan site anahtarıyla
    BİRLİKTE yazılıyor ve `recaptcha.service.ts` oradan okuyor.

    Kataloğa da koysaydık ikinci bir ev açılırdı: konsoldan değiştiren
    kişi bir şey yaptığını sanır, servis öteki evden okumaya devam
    ederdi. Tam olarak bu depoda günlerce avladığımız sessiz kusur.

    Eşiğin anahtarlarla aynı yerde olması ayrıca DOĞRU: anahtarsız bir
    eşik anlamsız ve ikisi birlikte yapılandırılıyor.

    F-SEC-02 "reCAPTCHA devreye girme eşiği" diyor; karşılığı var,
    yalnızca bu ekranda değil. Konsol ona BAĞLANTI verebilir.
  */

  // ---------------------------------------------------------------
  // F-SEC-03 — OTURUM POLİTİKASI
  // ---------------------------------------------------------------
  {
    kod: "oturum.bosta_dakika_personel",
    bolum: "oturum",
    ad: "Hareketsizlik süresi — personel",
    aciklama: "Personel oturumu, hiç istek gelmezse bu süre sonunda düşer.",
    birim: "dakika",
    varsayilan: 30,
    dogrulayici: pozitifTam("Süre"),
  },
  {
    kod: "oturum.bosta_dakika_ogrenci",
    bolum: "oturum",
    ad: "Hareketsizlik süresi — öğrenci ve veli",
    aciklama:
      "Öğrenci ve veli oturumu için aynı süre. Personelden uzun tutuluyor: " +
      "öğrenci telefonda ve gün içinde aralıklı giriyor.",
    birim: "dakika",
    varsayilan: 7 * 24 * 60,
    dogrulayici: pozitifTam("Süre"),
  },
  {
    kod: "oturum.mutlak_saat",
    bolum: "oturum",
    ad: "Mutlak oturum ömrü",
    aciklama:
      "Sürekli kullanılsa bile oturumun yaşayabileceği en uzun süre. " +
      "Hareketsizlik penceresini kaydırmak bunu aşamıyor.",
    birim: "saat",
    varsayilan: 720,
    dogrulayici: pozitifTam("Süre"),
  },
  {
    kod: "oturum.sudo_dakika",
    bolum: "oturum",
    ad: "Sudo penceresi",
    aciklama:
      "Şifre yeniden girildikten sonra hassas işlemlerin açık kalacağı süre " +
      "(korumalı not okuma, rol atama, kalıcı silme).",
    birim: "dakika",
    varsayilan: 5,
    dogrulayici: pozitifTam("Süre"),
  },
  {
    kod: "oturum.eszamanli_personel",
    bolum: "oturum",
    ad: "Eşzamanlı oturum sınırı — personel",
    aciklama:
      "Bir personelin aynı anda açık tutabileceği oturum sayısı. Sınır aşılınca " +
      "en eski oturum kapanıyor.",
    birim: "oturum",
    varsayilan: 3,
    dogrulayici: pozitifTam("Sınır"),
  },
  {
    kod: "oturum.dondurme_dakika",
    bolum: "oturum",
    ad: "Jeton döndürme aralığı",
    aciklama:
      "Oturum jetonunun yenilenme sıklığı. Çalınan bir çerezin işe yarayacağı " +
      "pencereyi belirliyor (G-SES-01).",
    birim: "dakika",
    varsayilan: 15,
    dogrulayici: pozitifTam("Aralık"),
  },
  {
    kod: "oturum.dondurme_tolerans_saniye",
    bolum: "oturum",
    ad: "Jeton döndürme toleransı",
    aciklama:
      "Yenilenen jetonun eskisinin de kabul edileceği kısa süre. Paralel " +
      "sekmelerin aynı anda yenilemesi kullanıcıyı dışarı atmasın diye.",
    birim: "saniye",
    varsayilan: 30,
    dogrulayici: pozitifTam("Süre"),
  },

  // ---------------------------------------------------------------
  // F-SEC-04 — KİMLİK, ŞİFRE VE CİHAZ
  // ---------------------------------------------------------------
  {
    kod: "kimlik.2fa_gecis_gun",
    bolum: "kimlik",
    ad: "2FA geçiş süresi",
    aciklama:
      "Bir rolde 2FA zorunlu yapıldığında kullanıcının kurulum için kaç günü " +
      "olduğu. Süre dolunca kurulum zorunlu sayılıyor.",
    birim: "gün",
    varsayilan: 7,
    dogrulayici: pozitifTam("Süre"),
  },
  {
    kod: "kimlik.yedek_kod_sayisi",
    bolum: "kimlik",
    ad: "Yedek kod sayısı",
    aciklama:
      "2FA kurulumunda üretilen tek kullanımlık kod adedi. Hepsi bitince hesabı " +
      "yalnızca yönetici açabiliyor.",
    birim: "kod",
    varsayilan: 10,
    dogrulayici: pozitifTam("Sayı"),
  },
  {
    kod: "kimlik.sifre_sifirlama_dakika",
    bolum: "kimlik",
    ad: "Şifre sıfırlama bağlantısı ömrü",
    aciklama:
      "Sıfırlama bağlantısının geçerli kalacağı süre. Uzatmak, e-postası ele " +
      "geçirilmiş bir hesap için pencereyi büyütür.",
    birim: "dakika",
    varsayilan: 30,
    dogrulayici: pozitifTam("Süre"),
  },
  {
    kod: "kimlik.guvenilir_cihaz_gun",
    bolum: "kimlik",
    ad: "Güvenilir cihaz ömrü",
    aciklama:
      "“Bu cihazı hatırla” seçildiğinde 2FA'nın kaç gün sorulmayacağı " +
      "(G-AUT-01c). Bu süre boyunca çalınan bir cihaz jetonu 2FA'yı atlıyor.",
    birim: "gün",
    varsayilan: 30,
    dogrulayici: pozitifTam("Süre"),
  },
];

const KOD_HARITASI = new Map(GUVENLIK_AYARLARI.map((a) => [a.kod, a]));

/** Katalogda var mı — kataloğa eklenmemiş bir anahtar YAZILAMAZ. */
export function ayarTanimi(kod: string): GuvenlikAyariTanimi | undefined {
  return KOD_HARITASI.get(kod);
}

export type AyarDegerleri = Readonly<Record<string, number>>;

/** Hiçbir sapma yokken geçerli olan değerler. */
export function varsayilanAyarlar(): AyarDegerleri {
  return Object.fromEntries(GUVENLIK_AYARLARI.map((a) => [a.kod, a.varsayilan]));
}

export type AyarDogrulama = { gecerli: true; deger: number } | { gecerli: false; mesaj: string };

/**
 * Tek bir ayarın değerini doğrular.
 *
 * Katalogda olmayan kod **reddediliyor**: izin kataloğunun aynı kuralı.
 * Kabul etseydik yazım hatası içeren bir anahtar veritabanına düşer,
 * hiçbir zaman okunmaz ve "ayarladım ama çalışmıyor" diye görünürdü.
 */
export function ayarDogrula(kod: string, ham: unknown): AyarDogrulama {
  const tanim = ayarTanimi(kod);
  if (!tanim) return { gecerli: false, mesaj: "Tanınmayan ayar." };

  const sonuc = tanim.dogrulayici.safeParse(ham);
  if (!sonuc.success) {
    return { gecerli: false, mesaj: sonuc.error.issues[0]?.message ?? "Geçersiz değer." };
  }
  return { gecerli: true, deger: sonuc.data };
}

/**
 * BİRBİRİNİ DIŞLAYAN AYARLAR — F-SEC-07
 *
 * Tek tek geçerli olan iki değer BİRLİKTE geçersiz olabilir. Taban
 * kilit süresi azami kilit süresinden büyükse üstel artış hiç
 * çalışmaz: ilk kilit zaten tavanı aşmış olur ve "artan ceza" diye bir
 * şey kalmaz.
 *
 * Bu kontrol **tek tek doğrulamanın içine yazılamaz**: o fonksiyon
 * yalnızca bir alanı görüyor. Ayar yazan her yol bunu çağırmak zorunda
 * ve çağırmayan yol, sessizce tutarsız bir çift yazabilir — bu yüzden
 * yazma servisi tek kapıdan geçiyor.
 */
export function capraziDogrula(degerler: AyarDegerleri): string | null {
  const taban = degerler["giris.taban_kilit_dakika"];
  const azami = degerler["giris.azami_kilit_dakika"];
  if (taban !== undefined && azami !== undefined && taban > azami) {
    return "Taban kilit süresi, azami kilit süresinden büyük olamaz.";
  }

  const bostaPersonel = degerler["oturum.bosta_dakika_personel"];
  const mutlakSaat = degerler["oturum.mutlak_saat"];
  if (bostaPersonel !== undefined && mutlakSaat !== undefined && bostaPersonel > mutlakSaat * 60) {
    /*
      Hareketsizlik süresi mutlak ömürden uzunsa hareketsizlik penceresi
      HİÇ devreye giremez — oturum her zaman mutlak ömürden düşer.
      Ayarı yapan kişi "iki saatlik hareketsizlik" yazdığını sanır,
      oysa yazdığı şeyin hiçbir etkisi yoktur.
    */
    return "Hareketsizlik süresi, mutlak oturum ömründen uzun olamaz — o hâlde hiç devreye girmez.";
  }

  const dondurme = degerler["oturum.dondurme_dakika"];
  if (dondurme !== undefined && bostaPersonel !== undefined && dondurme > bostaPersonel) {
    // Jeton, yenilenmeye fırsat bulamadan oturum düşer: döndürme
    // koruması (G-SES-01) hiç çalışmaz.
    return "Jeton döndürme aralığı, hareketsizlik süresinden uzun olamaz.";
  }

  return null;
}
